import { Link } from "react-router-dom";
import { Star, Heart, MessageCircle, ShoppingCart, User, Award } from "lucide-react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Product } from "@/src/types";
import { getWhatsAppLink, shortenUrl } from "@/src/lib/utils";
import { doc, getDoc } from "firebase/firestore";
import { db as firestoreDb } from "@/src/lib/firebase";
import { toast } from "sonner";
import React from "react";
import { useCart } from "@/src/lib/CartContext";
import { useWishlist } from "@/src/lib/WishlistContext";

import { useAuth } from "@/src/lib/AuthContext";

interface ProductCardProps {
  product: Product;
  shopWhatsApp?: string;
  shopName?: string;
  key?: string | number;
}

export function ProductCard({ product, shopWhatsApp, shopName }: ProductCardProps) {
  const [loading, setLoading] = React.useState(false);
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { user } = useAuth();

  const isFavorite = isInWishlist(product.id);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await addToCart(product);
  };

  const handleToggleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await toggleWishlist(product.id);
  };

  const handleWhatsAppPurchase = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    let whatsapp = shopWhatsApp;
    let currentShopName = shopName;
    
    if (!whatsapp || !currentShopName) {
      setLoading(true);
      try {
        const shopDoc = await getDoc(doc(firestoreDb, "shops", product.shopId));
        if (shopDoc.exists()) {
          const shopData = shopDoc.data();
          whatsapp = whatsapp || shopData.whatsapp;
          currentShopName = currentShopName || shopData.name;
        }
      } catch (error) {
        console.error("Error fetching shop info:", error);
      } finally {
        setLoading(false);
      }
    }

    if (whatsapp) {
      try {
        setLoading(true);
        const productUrl = `${window.location.origin}/product/${product.id}`;
        const shortUrl = await shortenUrl(
          productUrl, 
          currentShopName, 
          product.name,
          product.images?.[0], // Pass image
          product.description // Pass description
        );
        const priceText = product.priceToAgree ? "Precio a convenir" : `$${product.price.toLocaleString()}`;
        const message = `Hola! Me interesa este producto: ${product.name}\nPrecio: ${priceText}\nLink: ${shortUrl}`;
        window.open(getWhatsAppLink(whatsapp, message), "_blank");
      } catch (error) {
        console.error("Error shortening URL:", error);
        const priceText = product.priceToAgree ? "Precio a convenir" : `$${product.price.toLocaleString()}`;
        const message = `Hola! Me interesa este producto: ${product.name}\nPrecio: ${priceText}\nLink: ${window.location.origin}/product/${product.id}`;
        window.open(getWhatsAppLink(whatsapp!, message), "_blank");
      } finally {
        setLoading(false);
      }
    } else {
      toast.error("Esta tienda no tiene WhatsApp configurado");
    }
  };

  return (
    <Card className="overflow-hidden group hover:shadow-md transition-all duration-300">
      <div className="relative aspect-square overflow-hidden bg-muted">
        <Link to={`/product/${product.id}`}>
          <img
            src={product.images[0] || `https://picsum.photos/seed/${product.id}/400/400`}
            alt={product.name}
            className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-500"
            referrerPolicy="no-referrer"
          />
        </Link>
        
        {/* Quick Add to Cart Overlay */}
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center pointer-events-none group-hover:pointer-events-auto">
          <Button 
            size="sm" 
            onClick={handleAddToCart}
            disabled={product.stock === 0 || loading}
            className="bg-white text-black hover:bg-white/90 rounded-full font-bold shadow-xl translate-y-4 group-hover:translate-y-0 transition-all duration-300"
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            + Carrito
          </Button>
        </div>

        {product.isClubExclusive && (
          <Badge className="absolute top-2 left-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white border-none gap-1 shadow-lg">
            <Award className="h-3 w-3" /> Socio Club
          </Badge>
        )}
        <Button
          variant="ghost"
          size="icon"
          className={`absolute top-2 right-2 backdrop-blur-sm rounded-full h-8 w-8 transition-colors ${
            isFavorite ? "bg-red-50 text-red-500 hover:bg-red-100" : "bg-background/80 hover:bg-background"
          }`}
          onClick={handleToggleWishlist}
        >
          <Heart className={`h-4 w-4 ${isFavorite ? "fill-current" : ""}`} />
        </Button>
        {product.stock === 0 && (
          <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
            <Badge variant="destructive">Agotado</Badge>
          </div>
        )}
      </div>
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-1">
          <Link to={`/product/${product.id}`}>
            <h3 className="font-semibold text-sm line-clamp-1 hover:text-primary transition-colors">
              {product.name}
            </h3>
          </Link>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center text-sm font-medium">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 mr-1" />
            {(product.rating || 0).toFixed(1)}
          </div>
          <span className="text-xs text-muted-foreground">({product.reviewCount || 0})</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-bold text-lg">
            {product.priceToAgree ? "Precio a convenir" : `$${product.price.toLocaleString()}`}
          </span>
          <Badge variant="outline" className="text-[10px]">
            {product.type === "product" ? "Producto" : "Servicio"}
          </Badge>
        </div>
      </CardContent>
      <CardFooter className="p-4 pt-0 flex flex-col gap-2">
        {user ? (
          <>
            <Button 
              className="w-full gap-2" 
              variant="outline"
              size="sm" 
              disabled={product.stock === 0 || loading}
              onClick={handleAddToCart}
            >
              <ShoppingCart className="h-4 w-4" />
              Agregar al Carrito
            </Button>
            <Button 
              className="w-full gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white border-none" 
              size="sm" 
              disabled={product.stock === 0 || loading}
              onClick={handleWhatsAppPurchase}
            >
              <MessageCircle className="h-4 w-4" />
              Comprar por WhatsApp
            </Button>
          </>
        ) : (
          <Button 
            className="w-full gap-2" 
            variant="default"
            size="sm"
            onClick={() => window.location.href = '/auth'}
          >
            <User className="h-4 w-4" />
            Inicia sesión para comprar
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
